import { IPublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiScopes } from "../auth/authConfig";
import type {
  ReviewItem,
  CuentaCatalogo,
  ProyectoCatalogo,
  CompradorOption,
  AutorizarContaduriaPayload,
  AutorizarCompradorPayload,
  AutorizarCompradorResultado,
  RechazarPayload,
  LotePayload,
  LoteContaduriaPayload,
  LoteResultado,
  LineaFactura,
  CfeFueraDeCarrilA,
  ContadoresBandejas,
  FacturaParaPagar,
  LoteBanco,
  ReviewItemArchivado,
  EventoHistorial,
  RespuestaBusqueda,
  CriteriosBusqueda,
} from "../types/reviewItem";

/**
 * Cliente de la API de cfe-review-api.
 *
 * ⚠️ IMPORTANTE — endpoints confirmados vs. a verificar:
 * Los 13 endpoints de abajo están confirmados contra README.md/CLAUDE.md/
 * Decisions.md del backend (05/08/2026). El backend menciona "18 rutas HTTP
 * totales" en un lugar y "10 rutas HTTP" (antes de sumar 3) en otro — no
 * hay una lista exhaustiva y numerada de las 18 en los documentos que se
 * revisaron. NO asumir que este cliente está completo solo porque compila
 * — antes de dar por cerrado el scaffolding, confirmar contra
 * `function_app.py` real (grep de `@app.route`) que no falta ningún
 * endpoint, y agregarlo acá si aparece uno nuevo.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

async function getAccessToken(msalInstance: IPublicClientApplication): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error("No hay una cuenta activa — el usuario no está logueado.");
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // El silent falló (token expirado y no se pudo refrescar sin
      // interacción) — hace falta un popup/redirect. Se deja explícito en
      // vez de fallar en silencio.
      const result = await msalInstance.acquireTokenPopup({ scopes: apiScopes, account });
      return result.accessToken;
    }
    throw err;
  }
}

/**
 * Reintento ante cold start — [NUEVO 08/08/2026, ver docs/Decisions.md]
 *
 * Caso real: al abrir la app un sábado apareció "Error: Backend call failure"
 * con un 500. El backend nunca falló (todas las llamadas dieron 200 en
 * 420–807 ms cuando ya estaba caliente): el problema es el arranque en frío
 * de la Function App + la reactivación de Azure SQL Serverless (auto-pause a
 * los 60 min), que fuera del horario de los Timers puede exceder el timeout
 * del proxy de Static Web Apps.
 *
 * ⚠️ SOLO se reintentan los GET. Los POST de autorizar/rechazar/comentar NO
 * son idempotentes: si un POST llega al backend y la respuesta se pierde por
 * timeout, reintentarlo duplicaría la acción (un comentario repetido, o peor,
 * una aprobación contada dos veces). Ante un POST fallido se propaga el error
 * y decide la persona. Esto alcanza para el caso real: las 5 llamadas de la
 * pantalla inicial son todas GET.
 */
/**
 * [AJUSTADO 11/08/2026] Se sumó un primer reintento casi inmediato (600 ms).
 *
 * Motivo, con datos de producción: el 500 NO viene del backend — todas sus
 * invocaciones de ese día terminaron OK, la mayoría en ~500 ms (el Health
 * Check lo mantiene caliente pegándole cada ~20 s). El que arranca en frío es
 * el proxy de la Static Web App, que es una Function App aparte y sin
 * warm-up. Cuando está frío devuelve el 500 **rápido**, así que esperar 2 s
 * antes del primer reintento era tiempo regalado: para entonces la instancia
 * suele estar levantada. Las esperas más largas quedan como red de seguridad
 * para el caso en que además haya que reactivar Azure SQL Serverless.
 */
const ESPERAS_REINTENTO_MS = [600, 2000, 5000];
const STATUS_REINTENTABLES = [500, 502, 503, 504];

const esperar = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function request<T>(
  msalInstance: IPublicClientApplication,
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken(msalInstance);
  const metodo = (options.method ?? "GET").toUpperCase();
  const reintentable = metodo === "GET";
  const intentosMaximos = reintentable ? ESPERAS_REINTENTO_MS.length + 1 : 1;

  let ultimoError: unknown;

  for (let intento = 0; intento < intentosMaximos; intento++) {
    if (intento > 0) await esperar(ESPERAS_REINTENTO_MS[intento - 1]);

    try {
      const resp = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          // NUNCA "Authorization" acá — Azure Static Web Apps sobrescribe ese
          // header exacto con un token interno propio antes de que llegue a la
          // función proxy (bug/limitación documentada de la plataforma, no de
          // este código — ver docs/Decisions.md, 07/08/2026). Se manda en un
          // header con otro nombre; el proxy (api/) y el proxy de Vite en dev
          // lo traducen de vuelta a "Authorization" recién al reenviar al
          // backend real, que sí lo espera con el nombre estándar.
          "X-Authorization": `Bearer ${token}`,
          ...(options.headers ?? {}),
        },
      });

      if (!resp.ok) {
        // 401/403 más probablemente indican: token sin el rol correcto para
        // este endpoint (ver docs/Decisions.md del backend, "Autenticación por
        // rol") — no un bug de este cliente. 409 en varios endpoints significa
        // "el review_item no está en el estado esperado" (alguien más ya lo
        // procesó) — el llamador debe refrescar la lista, no reintentar ciego.
        // Ninguno de esos se reintenta: no los arregla esperar.
        const body = await resp.text().catch(() => "");
        const error = new ApiError(resp.status, body || resp.statusText);
        if (reintentable && STATUS_REINTENTABLES.includes(resp.status) && intento < intentosMaximos - 1) {
          ultimoError = error;
          continue;
        }
        throw error;
      }

      if (resp.status === 204) return undefined as T;
      return (await resp.json()) as T;
    } catch (err) {
      // fetch lanza (en vez de devolver !ok) ante error de red — también
      // esperable durante un cold start. Un ApiError ya decidió arriba si
      // correspondía reintentar, así que acá se propaga tal cual.
      if (err instanceof ApiError) throw err;
      ultimoError = err;
      if (!reintentable || intento === intentosMaximos - 1) throw err;
    }
  }

  throw ultimoError;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

export function createApiClient(msalInstance: IPublicClientApplication) {
  return {
    // -- Listado -----------------------------------------------------------

    /** Confirmado: GET /review-items?paso=contaduria|comprador&filtro=... */
    listarReviewItems(
      paso: "contaduria" | "comprador",
      filtro:
        | "pendientes"
        | "aprobado_pendiente_posteo"
        | "en_sap"
        | "error_posteo"
        | "rechazadas" = "pendientes"
    ) {
      return request<ReviewItem[]>(msalInstance, `/review-items?paso=${paso}&filtro=${filtro}`);
    },

    /**
     * Confirmado: GET /review-items?filtro=fuera_de_carril_a (rol Contaduria
     * únicamente — el backend devuelve 403 con otro rol). Forma de fila
     * distinta a ReviewItem (ver CfeFueraDeCarrilA) — vw_cfe_fuera_carril_a,
     * no vw_review_items_pendientes.
     */
    listarFueraDeCarrilA() {
      return request<CfeFueraDeCarrilA[]>(msalInstance, `/review-items?filtro=fuera_de_carril_a`);
    },

    /** [NUEVO 20/10/2026] GET /review-items?filtro=archivadas — solo lectura, rol Contaduria. */
    listarArchivadas() {
      return request<ReviewItemArchivado[]>(msalInstance, `/review-items?filtro=archivadas`);
    },

    /** [NUEVO 20/10/2026] GET /review-items/{id}/historial — rol Contaduria. */
    obtenerHistorial(id: string) {
      return request<EventoHistorial[]>(msalInstance, `/review-items/${id}/historial`);
    },

    /**
     * [NUEVO 20/10/2026] GET /review-items/buscar. Detrás del flag del
     * backend: apagado responde 404. El backend acota lo que ve cada rol.
     */
    buscarReviewItems(criterios: CriteriosBusqueda) {
      const params = new URLSearchParams();
      for (const [clave, valor] of Object.entries(criterios)) {
        if (valor && valor.trim()) params.set(clave, valor.trim());
      }
      return request<RespuestaBusqueda>(msalInstance, `/review-items/buscar?${params.toString()}`);
    },

    /** [NUEVO 20/10/2026] GET /funcionalidades — qué funcionalidades opcionales están prendidas. */
    obtenerFuncionalidades() {
      return request<{ buscador: boolean; pagos_banco?: boolean }>(msalInstance, `/funcionalidades`);
    },

    /** Confirmado: GET /review-items/{id}/lineas (rol Contaduria o Comprador) */
    obtenerLineas(id: string) {
      return request<LineaFactura[]>(msalInstance, `/review-items/${id}/lineas`);
    },

    /**
     * GET /review-items/contadores — [NUEVO 08/08/2026] cantidad de filas por
     * bandeja, para los badges del menú. Existe para no tener que traer las 5
     * listas completas solo para contarlas (con el volumen real serían 5
     * payloads de cientos de filas en cada carga). `fuera_de_carril_a` viene
     * solo con rol Contaduría.
     */
    // -- Control de facturas para pagar (rol ControlPagos o Contaduria) -----

    /** Confirmado: GET /facturas-para-pagar */
    listarFacturasParaPagar() {
      return request<FacturaParaPagar[]>(msalInstance, `/facturas-para-pagar`);
    },

    /**
     * Vuelve a consultar SAP. Solo Contaduría.
     *
     * Es sincrónico y tarda ~11 s medidos contra producción, así que la
     * pantalla tiene que deshabilitar el botón mientras corre: dos cargas
     * simultáneas harían `DELETE` sobre la misma tabla a la vez.
     */
    sincronizarFacturasParaPagar() {
      return request<{
        filas_leidas: number;
        filas_cruzadas: number;
        fecha_corte: string;
        resultado: string;
      }>(msalInstance, `/facturas-para-pagar/sincronizar`, { method: "POST" });
    },

    /**
     * Marca o desmarca "Pago habilitado". La misma ruta sirve para una fila
     * (el click) y para varias (la barra de selección) — es idempotente.
     *
     * Las filas se identifican por `(trans_id, line_id)`, la clave del asiento
     * contable en SAP, y NO por `id`: ese se regenera en cada sincronización.
     *
     * `no_encontradas` viene con las filas que ya no están en el snapshot
     * (alguien sincronizó mientras la pantalla estaba abierta). El backend
     * devuelve 200 igual: hay que aplicar lo que sí entró y revertir solo esas.
     */
    marcarPagoHabilitado(
      filas: Array<{ trans_id: number; line_id: number }>,
      habilitado: boolean
    ) {
      return request<{
        actualizadas: number;
        no_encontradas: Array<{ trans_id: number; line_id: number }>;
      }>(msalInstance, `/facturas-para-pagar/pago-habilitado`, {
        method: "POST",
        body: JSON.stringify({ filas, habilitado }),
      });
    },

    // -- Lotes de pago del banco [NUEVO 03/11/2026] --------------------------

    /**
     * POST /lotes-pago-banco — Contaduría importa el comprobante PDF del lote.
     * Base64 dentro del JSON por el mismo motivo que el comprobante adjunto
     * (ver subirAdjunto). Reimportar el mismo lote actualiza su estado.
     */
    importarLotePagoBanco(payload: { pdf_base64: string; nombre_archivo: string }) {
      return request<{
        lote_id: number;
        id_transaccion: string;
        nuevo: boolean;
        estado: string | null;
        estado_anterior?: string | null;
        cantidad_pagos?: number;
        por_estado?: Record<string, number>;
      }>(msalInstance, `/lotes-pago-banco`, { method: "POST", body: JSON.stringify(payload) });
    },

    /** GET /lotes-pago-banco — los últimos lotes importados con su cruce. */
    listarLotesPagoBanco() {
      return request<LoteBanco[]>(msalInstance, `/lotes-pago-banco`);
    },

    /** GET /lotes-pago-banco/{id}/pdf — URL temporal del comprobante. */
    async obtenerPdfLoteBanco(id: number): Promise<string> {
      const r = await request<{ url: string }>(msalInstance, `/lotes-pago-banco/${id}/pdf`);
      return r.url;
    },

    obtenerContadores() {
      return request<ContadoresBandejas>(msalInstance, `/review-items/contadores`);
    },

    // -- Paso 1 — Contaduría (rol Contaduria) -------------------------------

    /** Confirmado: POST /review-items/{id}/autorizar-contaduria */
    autorizarContaduria(id: string, payload: AutorizarContaduriaPayload = {}) {
      return request<void>(msalInstance, `/review-items/${id}/autorizar-contaduria`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    /**
     * Confirmado: POST /review-items/autorizar-contaduria-lote
     *
     * [CAMBIADO 10/08/2026] El payload lleva `items` con la asignación de
     * Aprobador de cada fila, no solo `ids`. Antes las selecciones del
     * desplegable se perdían en el lote, y con el Aprobador vuelto obligatorio
     * en el backend eso habría hecho que el lote rechazara todo.
     *
     * La respuesta trae `sin_aprobador` con los que NO se autorizaron por no
     * tener Aprobador — hay que mostrárselo a quien apretó el botón, no
     * descartarlo en silencio.
     */
    autorizarContaduriaLote(payload: LoteContaduriaPayload) {
      return request<LoteResultado>(msalInstance, `/review-items/autorizar-contaduria-lote`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    /** Confirmado: POST /review-items/{id}/reabrir (RECHAZADA → PENDIENTE_CONTADURIA) */
    reabrir(id: string) {
      return request<void>(msalInstance, `/review-items/${id}/reabrir`, { method: "POST" });
    },

    /** [NUEVO 09/09/2026] RECHAZADA -> ARCHIVADA, rol Contaduria únicamente. */
    archivar(id: string) {
      return request<void>(msalInstance, `/review-items/${id}/archivar`, { method: "POST" });
    },

    /** Confirmado: POST /reintentar-posteo/{id} (rol Contaduria únicamente) */
    reintentarPosteo(id: string) {
      return request<void>(msalInstance, `/reintentar-posteo/${id}`, { method: "POST" });
    },

    // -- Paso 2 — Comprador/"Aprobador" (rol Comprador) ---------------------

    /** Confirmado: POST /review-items/{id}/comentario-comprador — INDEPENDIENTE de autorizar */
    comentarioComprador(id: string, comentario: string) {
      return request<void>(msalInstance, `/review-items/${id}/comentario-comprador`, {
        method: "POST",
        body: JSON.stringify({ comentario }),
      });
    },

    /**
     * Confirmado: POST /review-items/{id}/autorizar-comprador
     * [AMPLIADO 19/08/2026] Antes devolvía 204 sin cuerpo (`void`); ahora
     * siempre 200 con `{advertencia}` — request() ya sabe hacer JSON.parse
     * del cuerpo para cualquier status distinto de 204, no hizo falta
     * tocarlo. `advertencia` viene no-null si la misma persona autorizó el
     * Paso 1 y el Paso 2 de esta factura (directores con ambos roles).
     */
    autorizarComprador(id: string, payload: AutorizarCompradorPayload = {}) {
      return request<AutorizarCompradorResultado>(msalInstance, `/review-items/${id}/autorizar-comprador`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    /**
     * Confirmado: POST /review-items/autorizar-comprador-lote
     * ⚠️ Gap conocido del backend (ver README.md): esta variante en LOTE no
     * hace el upsert de ultimo_comprador_asignado (la individual sí) — no es
     * un bug de este cliente, es una limitación documentada del backend.
     */
    autorizarCompradorLote(payload: LotePayload) {
      return request<LoteResultado>(msalInstance, `/review-items/autorizar-comprador-lote`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    // -- Ambos roles ---------------------------------------------------------

    /** Confirmado: POST /review-items/{id}/rechazar */
    rechazar(id: string, payload: RechazarPayload) {
      return request<void>(msalInstance, `/review-items/${id}/rechazar`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    /**
     * Confirmado 07/08/2026 contra el backend real (curl + token real): el
     * campo es "url", no "sas_url" — bug real encontrado en producción, el
     * botón de PDF/XML abría "/undefined" (ver docs/Decisions.md).
     */
    async obtenerBlobSasUrl(id: string, tipo: "xml" | "pdf" | "adjunto"): Promise<string> {
      const result = await request<{ url: string }>(
        msalInstance,
        `/review-items/${id}/blob-sas?tipo=${tipo}`
      );
      return result.url;
    },

    /**
     * POST /review-items/{id}/adjunto — comprobante adicional opcional.
     * [NUEVO 08/09/2026]
     *
     * El archivo va como base64 dentro del JSON, así que usa el mismo
     * `request()` que todo el resto sin tocarle los headers. NO usar
     * multipart/form-data: el proxy same-origin de `api/` lee el body con
     * `request.text()` (UTF-8) y corrompería un binario — ver
     * src/utils/archivoAdjunto.ts y docs/Decisions.md.
     */
    subirAdjunto(id: string, payload: { adjunto_base64: string; adjunto_nombre: string }) {
      return request<{ adjunto_nombre_original: string }>(
        msalInstance,
        `/review-items/${id}/adjunto`,
        { method: "POST", body: JSON.stringify(payload) }
      );
    },

    // -- Desplegables (Paso 1) ------------------------------------------------

    /** Confirmado: GET /catalogo-cuentas (rol Contaduria) */
    listarCatalogoCuentas() {
      return request<CuentaCatalogo[]>(msalInstance, `/catalogo-cuentas`);
    },

    /**
     * Confirmado: GET /catalogo-proyectos — rol Contaduria **o Comprador**,
     * a diferencia de los otros dos catálogos (solo Contaduria). Lo consume
     * el Aprobador en su propia pantalla, así que el backend tuvo que abrir
     * el gate: con 403 acá, el `cargar()` de CompradorPage vaciaría la página
     * entera en vez de solo el desplegable.
     */
    listarCatalogoProyectos() {
      return request<ProyectoCatalogo[]>(msalInstance, `/catalogo-proyectos`);
    },

    /** Confirmado: GET /compradores (rol Contaduria) */
    listarCompradores() {
      return request<CompradorOption[]>(msalInstance, `/compradores`);
    },

    // -- Utilidad --------------------------------------------------------------

    /** Confirmado: POST /matriz/sincronizar — sin _requiere_rol(), alcanza con estar autenticado */
    sincronizarMatriz() {
      return request<{ resultados: unknown; resultado: string }>(msalInstance, `/matriz/sincronizar`, {
        method: "POST",
      });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
